<template>
  <div
    ref="planActionItem"
    class="plan-action-item"
    :class="mainEltClasses"
    @click="clickMiddle"
  >
    <!-- Action details -->
    <div class="plan-action-item__container" :class="draggableClass">
      <img
        v-if="canDrag"
        class="plan-action-item__drag-indicator"
        src="@/assets/img/drag-indicator-gray.png"
        alt=""
      >
      <div class="plan-action-item__top-container">
        <Pdca
          ref="pdca"
          v-model="action.pdca"
          :pdca-id="action.id"
          :disabled="!isPDCAEditable"
          :quick-action="Number(action.isQuickAction) === 1"
          @input="pdcaInput"
        />
        <UserThumbnail
          :value="Number(action.leader)"
          :all-users="allUsers"
          show-name
          fetch-data
          :disabled="disabled || (!plan && !isQuickAction) || (plan ? plan.support == 1 : true)"
          :placeholder="decodeHTML(teamName)"
          @input="editLeader"
          @startEdit="$emit('startLeaderEdit')"
          @endEdit="$emit('endLeaderEdit')"
        />
      </div>
      <p class="plan-action-item__action-name">{{ action.name }}</p>
      <div class="plan-action-item__deadline f-w">
        <div v-if="!min" class="deadline-wrapper f-w">
          <div>
            <div class="f-h" :class="deadlineClasses" />
          </div>
          <img :src="require(`@/assets/img/${currentDeadlineIcon}.svg`)" alt="">
          <Icon class="plan-action-item__deadline__calendar">
            {{ scheduleMedium }}
          </Icon>
          <span class="deadline">{{ deadline }}</span>
        </div>
        <div v-if="tiptapTasks !== ''" class="v-centerer field-hint tiptap-tasks">
          <Icon remix-icon="checkbox-line" class="plan-action-item__deadline__tasks-icon" />
          <span>{{ tiptapTasks }}</span>
        </div>
      </div>
      <!-- Problem name -->
      <div
        v-if="action.problem > 0 && !hideProblemData"
        class="plan-action-item__problem"
        @click.stop="$router.push(getPbSheetLink(action.problem))"
      >
        <Icon class="plan-action-item__deadline__calendar">
          {{ archivesMedium }}
        </Icon>
        <div>{{ problemName }}</div>
      </div>
      <!-- Cloak to filter out actions -->
      <div v-if="shouldCloak" class="cloak" @click="clickMiddle" />
    </div>

    <div
      v-if="deleteBtnOnHover"
      class="plan-action-item__delete-btn generic-border generic-border--icon-button generic-border--red"
      @click.stop="$emit('deleteClick')"
    >
      <Icon>{{ deleteMedium }}</Icon>
    </div>
  </div>
</template>

<script>
/**
 * Action item in an action plan
 *
 * @vue-prop {Object} action - Action object from DB
 * @vue-prop {Array} allUsers - users array to display the leader thumbnail
 * @vue-prop {Boolean} min - if set and true, the deadline and the leader will be hidden (used in detailed indicator)
 * @vue-prop {Boolean} dashboard - if set to true, the leader name will be hidden and the height will be greatly reduced
 * @vue-prop {Boolean} disabled - disable pdca and leader edition
 * @vue-prop {Array} filters - filter to know if the item should be cloaked. Array of user objects
 * @vue-prop {Boolean} canDrag - make this coponent draggable
 * @vue-prop {Object} plan - action plan object (used to detect support action)
 * @vue-prop {Number} nLines - Company setting set to display a maximum number of lines n the component
 * @vue-prop {Boolean} [deleteBtnOnHover] - Display a delete button when hovering the item
 * @vue-prop {Boolean} [emitClick] - Bubble the click event instead of displaying the action creation modal
 */

import Icon from '@/components/icon.vue';
import Pdca from '@/components/pdca.vue';
import UserThumbnail from '@/components/user-thumbnail.vue';
// Mixins
import mixinCommonColors from '@/mixins/common-colors.vue';
// Socket utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Vuex utils
import { GET_TEAMS } from '@/store/mutation-types';
import { EDIT_ACTION } from '@/store/modules/problem/action-types';
// Utils
import { decodeHTML } from '@/utils/main';
import { archivesMedium, deleteMedium, scheduleMedium } from '@/assets/icons';
import { getTasksFromTiptapContent } from '@/plugins/tiptap';
import { getActionWorkflowStep, isActionCompleted } from '@/utils/action-utils';

export default {

  name: 'PlanActionItem',

  components: {
    Icon,
    Pdca,
    UserThumbnail,
  },

  mixins: [mixinCommonColors, mixinSocketHandler],

  props: {
    action: {
      type: Object,
      required: true,
    },
    allUsers: {
      type: Array,
      default: () => [],
    },
    min: Boolean,
    dashboard: Boolean,
    disabled: Boolean,
    filters: {
      type: Array,
      default: null,
    },
    canDrag: Boolean,
    plan: {
      type: Object,
      default: null,
    },
    nLines: {
      type: Number,
      default: 0,
    },
    isDragging: Boolean,
    isQuickAction: Boolean,
    hideProblemData: Boolean,
    deleteBtnOnHover: Boolean,
    emitClick: Boolean,
  },

  data() {
    return {
      archivesMedium,
      deleteMedium,
      scheduleMedium,
      itemHeight: 0,
    };
  },

  computed: {
    /** Object: CSS classes applied to this.$el */
    mainEltClasses() {
      return {
        min: this.min,
        'plan-action-wrapper__dashboard': this.dashboard,
        [`plan-action-item--step-${getActionWorkflowStep(this.action).ref}`]: true,
        'plan-action-item--has-problem': this.action.problem > 0,
      };
    },
    isPDCAEditable() {
      // If not in support plan, PDCA is editable
      return (this.action.supportPlan === null || this.action.supportPlan === undefined);
    },
    deadlinePercentage() {
      const added = this.$moment(this.action.added);
      const deadline = this.$moment(this.action.deadline);
      const range = this.$moment.duration(deadline.diff(added));
      const diff = this.$moment.duration(this.$moment().diff(added));
      if (range <= 0) return 100;
      return diff / range * 100;
    },
    /** Boolean: Whether the action is late or not */
    actionIsLate() {
      return !isActionCompleted(this.action) && this.deadlinePercentage >= 100;
    },
    /** Array: Array of classes to apply to the deadline div element */
    deadlineClasses() {
      const classes = ['deadline-fill'];
      // Determine width (fill percentage)
      const percentage = (this.deadlinePercentage > 0
        ? Math.min(this.deadlinePercentage, 100)
        : 0);
      classes.push(`perc${percentage - (percentage % 10)}`);
      // Determine bar color
      if (isActionCompleted(this.action)) {
        classes.push('green');
      } else if (this.actionIsLate) {
        classes.push('red');
      } else {
        classes.push('gray');
      }
      return classes;
    },
    added() {
      return this.$moment(this.action.added).format(this.$t('minDateFormat'));
    },
    deadline() {
      const mDeadline = this.$moment(this.action.deadline);
      return mDeadline.isValid() ? mDeadline.format(this.$t('monthDateFormat')) : '?';
    },
    shouldCloak() {
      return this.filters && !this.filters.includes(this.action.leader || 0);
    },
    /** Can be changed in settings */
    actionNameStyle() {
      if (this.nLines) {
        return {
          'max-height': `${Math.floor(this.nLines) * 1.5}em`,
        };
      }
      return {};
    },
    /** Team name to display if the leader is not set */
    teamName() {
      if (this.$store.state[GET_TEAMS.loadingKey]) {
        return '';
      }
      const team = (this.$store.state[GET_TEAMS.stateKey] || []).find(t => t.id === this.action.affectTeam);
      return team ? team.name : '';
    },
    /** String: Display the number of tasks added to the 'comment' field */
    tiptapTasks() {
      const tasks = getTasksFromTiptapContent(this.action.comment || '');
      return tasks ? `${tasks.n}/${tasks.total}` : '';
    },
    currentDeadlineIcon() {
      if (isActionCompleted(this.action)) {
        return 'deadline-valid';
      }
      if (this.actionIsLate) {
        return 'deadline-warning';
      }
      return 'deadline-in-progress';
    },
    problemName() {
      return `${this.$t('message.PROBLEM')}${this.$t('message.TWO_DOTS')}${decodeHTML(this.action.lWhat)}`;
    },
    draggableClass() {
      return {
        'item-handle': this.canDrag,
      };
    },
  },

  methods: {
    decodeHTML,
    pdcaInput(val) {
      this.$emit('pdcaEdit', val);
    },
    clickMiddle(evt) {
      if (this.emitClick) {
        this.$emit('click', evt);
        return;
      }
      if (this.isDragging) return;
      this.$router.push(this.getActionCreationLink(this.action.id));
    },
    editLeaderRadio() {
      this.$refs['radio-leader'].edit();
    },
    editLeader(newLeader) {
      const { id } = this.action;
      this.$store.dispatch(`problem/${EDIT_ACTION}`, { id, leader: newLeader }).then(({ data }) => {
        if (data instanceof Object) {
          this.$emit('leaderEdit', Number(newLeader));
          if (this.action.pdca !== data.pdca) {
            this.$emit('pdcaEdit', data.pdca);
          }
          this.sendSocketMessage(SocketMessages.CC_MSGS.ACTION_PLANS);
        } else {
          this.$t('message.CONNECTION_ERROR');
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.plan-action-item {
  $plan-item-padding: 0.7rem;

  position: relative;
  border-radius: $high-border-radius;
  margin-bottom: 15px;
  transition: box-shadow 0.15s;
  width: $action-item-width;
  cursor: pointer;

  --bg-color: #{$color-body-background};
  --border-color: #{$color-gray-background-hover};
  --bg-hover-color: #{$color-gray-background};

  &--step-plan {
    --bg-color: #{$color-pdca-plan-bg};
    --border-color: #{$color-pdca-plan-medium};
    --bg-hover-color: #{$color-pdca-plan-hover};
  }

  &--step-do {
    --bg-color: #{$color-pdca-do-bg};
    --border-color: #{$color-pdca-do-medium};
    --bg-hover-color: #{$color-pdca-do-hover};
  }

  &--step-check {
    --bg-color: #{$color-pdca-check-bg};
    --border-color: #{$color-pdca-check-medium};
    --bg-hover-color: #{$color-pdca-check-hover};
  }

  &--step-act {
    --bg-color: #{$color-pdca-act-bg};
    --border-color: #{$color-pdca-act-medium};
    --bg-hover-color: #{$color-pdca-act-hover};
  }

  background: var(--bg-color);
  border: solid 1px var(--border-color);

  &__container {
    padding: $plan-item-padding;
  }

  &--has-problem {
    .plan-action-item__container {
      padding-bottom: 0px;
    }
  }

  &__drag-indicator {
    width: 25px;
    position: absolute;
    top: 5px;
    left: 50%;
    transform: translateX(-50%);
  }

  &__top-container {
    display: flex;
    justify-content: space-between;
    height: 100%;
    margin-bottom: 5px;
    margin-top: 5px;
    align-items: center;

    .user-thumbnail {
      display: flex;
      align-items: center;

      &__avatar--initials {
        font-size: 1rem;
      }

      &__avatar {
        margin-left: 10px;
      }

      .editable-label-text {
        font-size: 0.85rem;
        text-align: right;
      }
    }
  }

  &__action-name {
    font-size: 1rem;
    font-weight: 600;
    margin-top: 10px;
    min-height: 10px;
    overflow-wrap: break-word;
  }

  & &__delete-btn.generic-border--icon-button {
    position: absolute;
    right: 0px;
    top: 50%;
    transform: translateY(-50%) scale(0.01);
    transition: transform .3s ease;
  }

  &:hover {
    background: var(--bg-hover-color);

    .plan-action-item__delete-btn.generic-border--icon-button {
      transform: translateY(-50%) translateX(50%) scale(0.9);
      transition: transform .3s ease;
    }
  }

  .cloak {
    position: absolute;
    top: 0px; bottom: 0px; left: 0px; right: 0px;
    background: rgba(0, 0, 0, 0.5);
  }

  &__deadline {
    border-collapse: collapse;
    margin-top: 5px;
    margin-bottom: 5px;
    table-layout: fixed;

    .deadline {
      white-space: nowrap;
      font-size: 0.85rem;
    }

    img {
      margin-right: 20px;
    }

    &__calendar {
      margin-right: 5px !important;
      width: 12px;
    }

    .tiptap-tasks {

      svg {
        color: $color-shizen-blue !important;
      }

      span {
        font-style: initial;
      }
    }

    td {
      vertical-align: middle;
    }

    .deadline-wrapper {
      display: flex;
      align-items: center;

      .added {
        margin-right: 5px;
      }

      > div {
        background:  #E4E4E8;
        height: 5px;
        overflow: auto;
        border-radius: 10px;
        width: 165px;
        margin-right: 10px;

        > div { border-radius: 10px; }
      }

      > svg {
        margin-right: 15px;
      }
    }
  }

  .deadline-fill.gray {
    background: #4C6174;
  }

  .deadline-fill.green {
    background: #2AA948;
    width: 100% !important;
  }

  .deadline-fill.red {
    background: #F15C61;
    width: 100% !important;
  }

  .deadline-fill.perc0 {
    width: 1%;
  }

  .deadline-fill.perc10 {
    width: 10%;
  }

  .deadline-fill.perc20 {
    width: 20%;
  }

  .deadline-fill.perc30 {
    width: 30%;
  }

  .deadline-fill.perc40 {
    width: 40%;
  }

  .deadline-fill.perc50 {
    width: 50%;
  }

  .deadline-fill.perc60 {
    width: 60%;
  }

  .deadline-fill.perc70 {
    width: 70%;
  }

  .deadline-fill.perc80 {
    width: 80%;
  }

  .deadline-fill.perc90 {
    width: 90%;
  }

  .deadline-fill.perc100 {
    width: 100%;
  }

  &.min .plan-action {
    height: 60px;
  }

  &__dashboard .plan-action {
    height: 60px;
    margin-top: 5px;

    .pdca {
      width: 60px;
    }

    .pdca-indicator {
      width: 40px !important;
      height: 40px !important;
    }
  }

  &__problem {
    font-size: 0.8em;
    text-decoration: underline;
    display: flex;
    margin-left: -$plan-item-padding;
    margin-right: -$plan-item-padding;
    border-top: solid 1px var(--border-color);
    padding: 6px 11px 8px 5px;
    align-items: center;
    box-sizing: border-box;

    svg {
      flex: 0 0 .8rem;
      width: .8rem;
      margin-right: 6px;
      margin-left: 8px;
    }

    div {
      flex: 1;
      min-width: 0px;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .plan-action-item {
    max-width: none;

    &__drag-indicator {
      display: none;
    }

    .plan-action {
      height: 60px;
    }

    &__top-container .user-thumbnail {
      width: initial;
    }

    &__dashboard .plan-action .pdca-indicator {
      width: 35px !important;
      height: 35px !important;
    }

    &__deadline {

      .deadline-wrapper > div {
        width: 100%;
      }
    }

    &__problem {

      svg {
        width: 14px;
      }
    }
  }
}
</style>
